import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '@/App';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import Layout from '@/components/Layout';
import { MessagesListSkeleton } from '@/components/SkeletonLoaders';
import { Send, User, ArrowLeft, Shield, Headphones } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Support user ID constant (must match backend)
const ADMIN_SUPPORT_ID = "consultanthub-support";
const ADMIN_SUPPORT_NAME = "ConsultantHUB Support";

const MessagesPage = () => {
  const { conversationId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (conversationId) {
      setSelectedConversation(conversationId);
      fetchMessages(conversationId);
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast.error('Errore nel caricamento delle conversazioni');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (convId) => {
    try {
      const response = await axios.get(`${API}/messages/${convId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Errore nel caricamento dei messaggi');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      // Get receiver ID from conversation ID
      const participantIds = selectedConversation.split('_');
      const receiverId = participantIds.find(id => id !== user.id);

      await axios.post(`${API}/messages`, {
        receiver_id: receiverId,
        content: newMessage
      });

      setNewMessage('');
      fetchMessages(selectedConversation);
      fetchConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Errore nell\'invio del messaggio');
    } finally {
      setSending(false);
    }
  };

  const selectConversation = (convId) => {
    setSelectedConversation(convId);
    fetchMessages(convId);
    navigate(`/messages/${convId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-8 mt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="mb-6">
            <h1 className="text-3xl md:text-4xl font-playfair font-bold text-[#344B71] mb-2" data-testid="messages-title">
              Messaggi
            </h1>
            <p className="text-slate-600">Le tue conversazioni con professionisti e aziende</p>
          </div>

          {conversations.length === 0 ? (
            <Card className="p-12 text-center bg-white" data-testid="no-conversations">
              <User className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">Non hai ancora nessuna conversazione</p>
              <Button onClick={() => navigate('/listings')} className="bg-[#344B71] hover:bg-[#344B71] text-white rounded-full">
                Esplora Annunci
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Conversations List */}
              <div className="lg:col-span-1">
                <Card className="p-4 bg-white h-[600px] overflow-y-auto">
                  <h2 className="text-lg font-semibold text-[#344B71] mb-4">Conversazioni</h2>
                  <div className="space-y-2">
                    {conversations.map((conv) => {
                      const otherUserId = conv.participant_ids.find(id => id !== user.id);
                      const otherUserName = conv.participant_names[otherUserId];
                      const isSelected = selectedConversation === conv.id;
                      const isSupport = otherUserId === ADMIN_SUPPORT_ID;

                      return (
                        <div
                          key={conv.id}
                          onClick={() => selectConversation(conv.id)}
                          className={`p-4 rounded-xl cursor-pointer transition-all ${
                            isSelected 
                              ? 'bg-gradient-to-br from-[#344B71] to-[#344B71] text-white shadow-lg' 
                              : isSupport
                                ? 'bg-emerald-50 hover:bg-emerald-100 text-[#344B71] border border-emerald-200'
                                : 'bg-slate-50 hover:bg-slate-100 text-[#344B71]'
                          }`}
                          data-testid={`conversation-item-${conv.id}`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${
                              isSelected 
                                ? 'bg-white text-[#344B71]' 
                                : isSupport 
                                  ? 'bg-emerald-600 text-white'
                                  : 'bg-gradient-to-br from-[#344B71] to-[#344B71] text-white'
                            }`}>
                              {isSupport ? (
                                <Headphones className="w-5 h-5" />
                              ) : (
                                otherUserName.charAt(0).toUpperCase()
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-sm truncate">
                                  {isSupport ? ADMIN_SUPPORT_NAME : otherUserName}
                                </p>
                                {isSupport && !isSelected && (
                                  <Shield className="w-3 h-3 text-emerald-600" />
                                )}
                              </div>
                              <p className={`text-xs truncate ${
                                isSelected ? 'text-slate-200' : 'text-slate-600'
                              }`}>
                                {conv.last_message}
                              </p>
                            </div>
                            {conv.unread_count > 0 && !isSelected && (
                              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs text-white font-medium ${
                                isSupport ? 'bg-emerald-600' : 'bg-[#E8A007]'
                              }`}>
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </div>

              {/* Messages Area */}
              <div className="lg:col-span-2">
                {selectedConversation ? (
                  <Card className="bg-white h-[600px] flex flex-col">
                    {/* Header */}
                    <div className="p-4 border-b border-slate-200">
                      {(() => {
                        const conv = conversations.find(c => c.id === selectedConversation);
                        if (!conv) return null;
                        const otherUserId = conv.participant_ids.find(id => id !== user.id);
                        const otherUserName = conv.participant_names[otherUserId];
                        const isSupport = otherUserId === ADMIN_SUPPORT_ID;
                        
                        return (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                                isSupport ? 'bg-emerald-600' : 'bg-gradient-to-br from-[#344B71] to-[#344B71]'
                              }`}>
                                {isSupport ? (
                                  <Headphones className="w-5 h-5" />
                                ) : (
                                  otherUserName.charAt(0).toUpperCase()
                                )}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold text-[#344B71]">
                                    {isSupport ? ADMIN_SUPPORT_NAME : otherUserName}
                                  </p>
                                  {isSupport && (
                                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full flex items-center gap-1">
                                      <Shield className="w-3 h-3" />
                                      Supporto Ufficiale
                                    </span>
                                  )}
                                </div>
                                {isSupport && (
                                  <p className="text-xs text-slate-500">Team ConsultantHUB</p>
                                )}
                              </div>
                            </div>
                            {!isSupport && (
                              <Button
                                onClick={() => {
                                  navigate(`/view-profile?user=${otherUserId}`);
                                }}
                                variant="outline"
                                size="sm"
                                className="border-[#344B71] text-[#344B71] hover:bg-[#344B71] hover:text-white rounded-full"
                                data-testid="view-profile-button"
                              >
                                Visualizza Profilo
                              </Button>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="messages-container">
                      {messages.map((msg) => {
                        const isOwn = msg.sender_id === user.id;
                        const isFromSupport = msg.sender_id === ADMIN_SUPPORT_ID;
                        
                        return (
                          <div
                            key={msg.id}
                            className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                            data-testid={`message-${msg.id}`}
                          >
                            <div className={`max-w-[70%] ${
                              isOwn 
                                ? 'bg-gradient-to-br from-[#344B71] to-[#344B71] text-white' 
                                : isFromSupport
                                  ? 'bg-emerald-50 text-slate-800 border border-emerald-200'
                                  : 'bg-slate-100 text-[#344B71]'
                            } rounded-2xl p-4`}>
                              {!isOwn && (
                                <div className="flex items-center gap-2 mb-1">
                                  <p className={`text-xs font-semibold ${isFromSupport ? 'text-emerald-700' : 'text-slate-600'}`}>
                                    {msg.sender_name}
                                  </p>
                                  {isFromSupport && (
                                    <Shield className="w-3 h-3 text-emerald-600" />
                                  )}
                                </div>
                              )}
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              <p className={`text-xs mt-2 ${
                                isOwn ? 'text-slate-200' : 'text-slate-500'
                              }`}>
                                {new Date(msg.timestamp).toLocaleTimeString('it-IT', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className="p-4 border-t border-slate-200">
                      <form onSubmit={handleSendMessage} className="flex space-x-2">
                        <Input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Scrivi un messaggio..."
                          className="flex-1 h-12 bg-white text-[#344B71] rounded-xl focus:border-[#344B71] focus:ring-[#344B71]"
                          data-testid="message-input"
                        />
                        <Button
                          type="submit"
                          disabled={sending || !newMessage.trim()}
                          className="bg-[#344B71] hover:bg-[#344B71] text-white rounded-xl px-6"
                          data-testid="send-button"
                        >
                          <Send className="w-5 h-5" />
                        </Button>
                      </form>
                    </div>
                  </Card>
                ) : (
                  <Card className="p-12 text-center bg-white h-[600px] flex items-center justify-center" data-testid="no-conversation-selected">
                    <div>
                      <User className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-600">Seleziona una conversazione per iniziare</p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </Layout>
  );
};

export default MessagesPage;
